import { Link, useParams } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { findIndustry } from "@/data/industries";

export default function IndustryDetail() {
  const { slug } = useParams();
  const ind = findIndustry(slug);

  if (!ind) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-6 text-center">
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#71717a] mb-3">
          404
        </div>
        <h1 className="font-heading text-3xl text-white">Industry not found</h1>
        <Link to="/industries" className="btn-outline mt-8">
          Back to industries
        </Link>
      </div>
    );
  }

  const Icon = ind.icon;

  return (
    <div>
      <section className="pt-16 md:pt-24 pb-20 md:pb-28 border-b border-[#27272a] relative overflow-hidden">
        <div className="absolute inset-0 radial-fade" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <Link
            to="/industries"
            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#71717a] hover:text-[#C8A24B] mb-6"
          >
            ← Industries
          </Link>
          <div className="grid lg:grid-cols-[1.2fr_1fr] gap-14 items-start">
            <div>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 border border-[#27272a] flex items-center justify-center">
                  <Icon size={22} className="text-[#C8A24B]" strokeWidth={1.4} />
                </div>
                <div className="text-[10px] tracking-[0.28em] uppercase text-[#C8A24B]">
                  Industry
                </div>
              </div>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white leading-[1.05]">
                {ind.title}
              </h1>
              <p className="mt-6 text-lg md:text-xl text-[#C8A24B] font-medium">
                {ind.lead}
              </p>
              <p className="mt-6 text-[#d4d4d8] leading-relaxed text-base md:text-lg max-w-2xl">
                {ind.intro}
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/contact" className="btn-gold">
                  Build my growth plan <ArrowRight size={16} />
                </Link>
                <Link to="/packages" className="btn-outline">
                  View packages
                </Link>
              </div>
            </div>
            <div className="border border-[#27272a] bg-[#0e0e0e] p-8 rounded-sm relative">
              <div className="absolute top-0 left-0 right-0 h-[1px] bg-[#C8A24B]" />
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#71717a] mb-4">
                Common Problems
              </div>
              <ul className="space-y-3">
                {ind.problems.map((p) => (
                  <li key={p} className="flex items-start gap-3">
                    <span className="w-1 h-1 rounded-full bg-[#C8A24B] mt-2.5 flex-shrink-0" />
                    <span className="text-[#d4d4d8]">{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-[#0d0d0d] border-b border-[#27272a]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-2 gap-14">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#71717a] mb-3">
              Recommended Services
            </div>
            <h2 className="font-heading text-3xl md:text-4xl text-white leading-tight">
              What we typically build for {ind.title.toLowerCase()}.
            </h2>
            <div className="mt-8 flex flex-wrap gap-2">
              {ind.services.map((s) => (
                <span
                  key={s}
                  className="px-3.5 py-2 text-sm border border-[#27272a] bg-[#0a0a0a] rounded-sm text-[#d4d4d8]"
                >
                  {s}
                </span>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#71717a] mb-3">
              Outcomes
            </div>
            <h2 className="font-heading text-3xl md:text-4xl text-white leading-tight">
              What our clients gain.
            </h2>
            <ul className="mt-8 space-y-3">
              {ind.outcomes.map((o) => (
                <li key={o} className="flex items-start gap-3">
                  <Check size={14} className="text-[#C8A24B] flex-shrink-0 mt-1" />
                  <span className="text-[#d4d4d8]">{o}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
